import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appearsInText, normalizeElementName, type ElementCategory } from '@scoutaround/script-breakdown-shared';

import { api, ApiError, type ReviewViewScene } from '../../lib/api.js';
import {
  ReviewRequirementsScreen,
  type ReviewRequirementsData,
  type ReviewRunState,
} from '../../components/ReviewRequirementsScreen.js';
import {
  AnalyseWithAi,
  extractionHasRun,
  parseFailure,
  scenesThatDidNotFit,
  scenesWithoutElements,
  shouldOfferAnalysis,
} from '../../components/AnalyseWithAi.js';
import { BreakdownProgress } from '../../components/BreakdownProgress.js';
import { ElementScopeDialog } from '../../components/ElementScopeDialog.js';
import {
  BulkSceneActions,
  type BulkSceneAction,
  type BulkSceneResult,
} from '../../components/BulkSceneActions.js';
import { useScriptRun } from '../../lib/useScriptRun.js';
import type {
  ElementDecision,
  RequirementDecision,
  SceneCardScene,
  SceneElement,
  SceneLine,
  SceneSpan,
} from '../../components/SceneCard.js';
import type { RailScene, SceneConfidence } from '../../components/SceneRail.js';
import type { ScriptProgress } from '../../lib/api.js';

/**
 * Split a scene's text into the spans the card renders, marking what the model
 * read where it read it.
 *
 * MATCHED BY TEXT, NOT BY OFFSET. The stored offsets are relative to whichever
 * artefact the span was cut from, and the scene body itself is a capped
 * excerpt — so an offset that is right for the document is wrong for this
 * string, and the failure is silent: the highlight lands on the wrong words and
 * still looks deliberate. Matching the mention's own text is stable under both,
 * and when the text genuinely is not there the mark is simply not drawn, which
 * is the honest outcome.
 */
/**
 * What the highlighter needs to paint one phrase. Narrower than a mark on
 * purpose: the text is painted from what is ON THE SHEET, which includes
 * elements a producer added by hand and excludes readings they removed — and
 * neither of those is a `ReviewViewMark`.
 */
interface Paintable {
  readonly text: string;
  readonly category: ElementCategory;
  readonly unconfirmed: boolean;
}

function markUp(text: string, marks: readonly Paintable[]): SceneSpan[] {
  if (marks.length === 0) return [{ text }];

  // Longest first: "FERRY DECK" must win over "FERRY", or the longer tag can
  // never match once the shorter one has consumed its opening words.
  const ordered = [...marks].sort((a, b) => b.text.length - a.text.length);

  let spans: SceneSpan[] = [{ text }];

  for (const mark of ordered) {
    if (mark.text.trim() === '') continue;

    spans = spans.flatMap((span) => {
      // Already marked — a word belongs to one tag, and re-splitting it would
      // nest marks the renderer cannot express.
      if (span.category !== undefined || span.facet !== undefined) return [span];

      const at = span.text.indexOf(mark.text);
      if (at === -1) return [span];

      const before = span.text.slice(0, at);
      const after = span.text.slice(at + mark.text.length);

      return [
        ...(before === '' ? [] : [{ text: before }]),
        { text: mark.text, category: mark.category, unconfirmed: mark.unconfirmed },
        ...(after === '' ? [] : [{ text: after }]),
      ];
    });
  }

  return spans;
}

/**
 * Screenplay layout from the text itself.
 *
 * A character cue is a short all-caps line; dialogue is what follows it until a
 * blank line. This is a rendering convenience, not a parse: the authoritative
 * structure is the screenplay file, and guessing wrong here costs alignment,
 * not meaning.
 */
function toLines(text: string, marks: readonly Paintable[]): SceneLine[] {
  const raw = text.split('\n');
  const lines: SceneLine[] = [];
  let inDialogue = false;

  for (const line of raw) {
    const trimmed = line.trim();

    if (trimmed === '') {
      inDialogue = false;
      lines.push({ kind: 'ACTION', spans: [{ text: '' }] });
      continue;
    }

    const isCue =
      trimmed.length <= 40 && trimmed === trimmed.toUpperCase() && /[A-Z]/.test(trimmed);

    const kind: SceneLine['kind'] = isCue
      ? 'CHARACTER'
      : trimmed.startsWith('(')
        ? 'PARENTHETICAL'
        : inDialogue
          ? 'DIALOGUE'
          : 'ACTION';

    if (isCue) inDialogue = true;

    lines.push({ kind, spans: markUp(line, marks) });
  }

  return lines;
}

function confidenceOf(scene: ReviewViewScene): SceneConfidence {
  if (scene.requirements.length === 0) return 'NOT_RECOGNISED';
  return scene.requirements.some((r) => r.confidenceBand !== 'HIGH')
    ? 'LOW_CONFIDENCE'
    : 'RESOLVED';
}

const UNDECIDED = new Set(['PROPOSED', 'DRAFT']);

/**
 * A failed AI reading, in words. The run's own code decides the sentence, and
 * none of them claims anything about the screenplay: a reading that did not
 * finish says nothing about what is in the script.
 */
export function extractionFailureSentence(progress: ScriptProgress | null): string {
  const code = progress?.failureCode ?? progress?.stages.find((s) => s.stage === 'EXTRACT')?.failureCode ?? null;

  if (code === 'EXTRACT_INTERRUPTED') {
    return 'The last AI breakdown was interrupted before it finished. You can run it again.';
  }
  if (code !== null && code.startsWith('MODEL_')) {
    return 'The last AI breakdown did not get a usable answer from the model. You can run it again.';
  }

  return 'The last AI breakdown did not finish. You can run it again.';
}

/** Where the reading stands, from the run's own record — never from a count. */
export function reviewRunStateOf(progress: ScriptProgress | null, inFlight: boolean): ReviewRunState {
  if (inFlight) return 'PROCESSING';
  if (progress?.status === 'FAILED' && parseFailure(progress) === null) return 'FAILED';
  if (scenesThatDidNotFit(progress) > 0 || scenesWithoutElements(progress) > 0) return 'PARTIAL';

  return extractionHasRun(progress) ? 'DONE' : 'NOT_STARTED';
}

/**
 * Review requirements, wired to the workspace.
 *
 * The screen itself is presentational and takes data; this is the half that
 * knows about the API, so the layout can be exercised without a server and the
 * fetching can change without touching it.
 */
export function ReviewRequirements({
  projectId,
  scriptVersionId,
  projectTitle,
  scriptVersionLabel,
  onContinueToMatching,
  continueBlockedReason = null,
  continueNote = null,
  focus = 'ALL',
  continueLabel,
  initialSceneId = null,
  onSceneChange,
}: {
  projectId: string;
  scriptVersionId: string;
  projectTitle: string;
  scriptVersionLabel: string;
  onContinueToMatching?: () => void;
  continueBlockedReason?: string | null;
  continueNote?: string | null;
  /** Which workflow this screen serves — see `ReviewRequirementsScreen`. */
  focus?: 'ALL' | 'LOCATIONS' | 'ELEMENTS';
  continueLabel?: string;
  /** The scene to open on — a route's `?scene=`. */
  initialSceneId?: string | null;
  /** Told when the scene on screen changes, so a route can keep it in the address. */
  onSceneChange?: (sceneId: string) => void;
}) {
  const queryClient = useQueryClient();

  /**
   * THIS SCREEN OWNS ITS OWN RE-READ, because on step 3 nothing else is
   * mounted to do it.
   *
   * The poll was added read-only on 2026-10-26, on the reasoning that the
   * screens owning this data pass their own `onFinished` — true of the Script
   * tab and of step 2, and false exactly here. A producer watched a run finish
   * COMPLETED with 85 requirements written while the page went on reading "0
   * location requirements across 120 scenes", because the answer on screen was
   * fetched before any of them existed. Invalidating twice when this is mounted
   * inside the Script tab is harmless — react-query folds concurrent refetches
   * of one key — and the cost of not doing it is a screen that lies.
   */
  //
  // UNTIL THAT RE-READ LANDS, THE RUN IS STILL "READING" (stage, 2026-10-30).
  // The run reads finished a moment before the new answer arrives, and in that
  // moment the screen held the pre-run answer — "No location requirements
  // identified", about a reading that had just found some.
  const [rereading, setRereading] = useState(false);
  const { progress, inFlight } = useScriptRun(scriptVersionId, {
    onFinished: () => {
      setRereading(true);
      void Promise.all([
        queryClient.invalidateQueries({ queryKey: ['review-view', scriptVersionId] }),
        queryClient.invalidateQueries({ queryKey: ['requirements', projectId] }),
        queryClient.invalidateQueries({ queryKey: ['review-progress', projectId] }),
        queryClient.invalidateQueries({ queryKey: ['project-setup', projectId] }),
      ]).finally(() => {
        setRereading(false);
      });
    },
  });
  const reading = inFlight || rereading;
  const didNotFit = scenesThatDidNotFit(progress);
  const withoutElements = scenesWithoutElements(progress);

  const review = useQuery({
    queryKey: ['review-view', scriptVersionId],
    queryFn: () => api.reviewView(scriptVersionId),
  });

  // The requirement rows themselves — the same query the Sourcing list runs.
  // The review view names a scene's requirements; this carries the row
  // version every approve and dismiss must present, and the status that is
  // current rather than the one the view was rendered from.
  const requirements = useQuery({
    queryKey: ['requirements', projectId],
    queryFn: () => api.listRequirements(projectId),
  });

  const refreshDecisions = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['review-view', scriptVersionId] }),
      queryClient.invalidateQueries({ queryKey: ['requirements', projectId] }),
      queryClient.invalidateQueries({ queryKey: ['review-progress', projectId] }),
      queryClient.invalidateQueries({ queryKey: ['project-setup', projectId] }),
    ]);
  };

  /**
   * Approve or dismiss — the verbs the Sourcing list has always had, on the
   * card where the scene's text is. Compare-and-swap on the row version, so a
   * decision made against a stale row is refused rather than overwriting
   * somebody else's; on that refusal the rows are re-read and the producer
   * decides again with the current one in front of them.
   */
  /** Undo a dismissal from the scene card: back to proposed, as step 2 does. */
  const restore = useMutation({
    mutationFn: (requirementId: string) => {
      const row = requirements.data?.find((r) => r.id === requirementId);
      if (row === undefined) throw new Error('That requirement is not loaded yet — try again in a moment.');

      return api.restoreRequirement(projectId, row.id, row.rowVersion);
    },
    onSuccess: refreshDecisions,
    onError: async (error) => {
      if (error instanceof ApiError && error.status === 409) await refreshDecisions();
    },
  });

  const decide = useMutation({
    mutationFn: (input: { requirementId: string; decision: RequirementDecision }) => {
      const row = requirements.data?.find((r) => r.id === input.requirementId);
      if (row === undefined) throw new Error('That requirement is not loaded yet — try again in a moment.');

      return input.decision === 'APPROVE'
        ? api.approveRequirement(projectId, row.id, row.rowVersion)
        : api.dismissRequirement(projectId, row.id, row.rowVersion);
    },
    onSuccess: refreshDecisions,
    onError: async (error) => {
      if (error instanceof ApiError && error.status === 409) await refreshDecisions();
    },
  });

  /**
   * THE COUNT OF LOCATIONS IS THE MODEL'S MOST EXPENSIVE MISTAKE — and neither
   * correction for it was wired to anything.
   *
   * The screen has had Merge and Split since it was built. Nothing passed
   * either handler, so both presses went nowhere even once something could be
   * selected. Two requirements that are really one place get scouted,
   * negotiated and booked twice; one that is really two leaves a production
   * with a location it never looked for.
   *
   * Both re-read everything afterwards: a merge moves scene links and
   * supersedes rows, a split creates a requirement and moves scenes off the
   * original, so the rail, the cards, the progress and the setup step are all
   * stale the moment either returns.
   */
  /**
   * The signal that closes the merge or split dialog: bumped only once the
   * server has agreed. Closing on the click threw away a selection the producer
   * would have to rebuild across a hundred and twenty cards if the write failed.
   */
  const [settled, setSettled] = useState(0);
  const landed = async () => {
    setSettled((n) => n + 1);
    await refreshDecisions();
  };

  const merge = useMutation({
    mutationFn: (requirementIds: readonly string[]) =>
      api.mergeRequirements(projectId, requirementIds),
    onSuccess: landed,
  });

  const split = useMutation({
    mutationFn: (input: {
      requirementId: string;
      title: string;
      sceneIds: readonly string[];
    }) =>
      api.splitRequirement(projectId, input.requirementId, {
        title: input.title,
        sceneIds: input.sceneIds,
      }),
    onSuccess: landed,
  });

  // A scene's review moves the detailed breakdown's status on the tracker, so
  // the setup answer is re-read with the view.
  const markReviewed = useMutation({
    mutationFn: (sceneId: string) => api.markSceneReviewed(projectId, sceneId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['review-view', scriptVersionId] }),
        queryClient.invalidateQueries({ queryKey: ['project-setup', projectId] }),
      ]);
    },
  });

  const unmarkReviewed = useMutation({
    mutationFn: (sceneId: string) => api.unmarkSceneReviewed(projectId, sceneId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['review-view', scriptVersionId] }),
        queryClient.invalidateQueries({ queryKey: ['project-setup', projectId] }),
      ]);
    },
  });

  /**
   * Keep or drop something the model read.
   *
   * The same verb the Script & Scenes breakdown panel has always used, on the
   * card where the scene's text is — so a producer reading the screenplay can
   * disagree where they noticed it, rather than somewhere else.
   */
  const decideElement = useMutation({
    mutationFn: (input: { mentionId: string; decision: ElementDecision }) =>
      api.decideElement(projectId, input.mentionId, { action: input.decision }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['review-view', scriptVersionId] });
    },
  });

  /**
   * How many of an element the scene needs.
   *
   * A MODEL PROPOSAL HAS NO COUNT TO SET. Counting is a judgement, and making
   * one is precisely what turns a reading into a line on the sheet — so a count
   * entered on a proposal accepts it first, which mints the production element
   * the number then hangs on. Doing it the other way round would need a
   * quantity column on the mention, where it would mean "the model's guess at
   * how many", which the model does not produce and nobody should read.
   */
  const setQuantity = useMutation({
    mutationFn: async (input: {
      sceneId: string;
      element: SceneElement;
      quantity: number | null;
    }) => {
      const id =
        input.element.provenance === 'HUMAN_CONFIRMED'
          ? input.element.id
          : (await api.decideElement(projectId, input.element.id, { action: 'ACCEPT' }))
              .productionElementId;

      if (id === null) throw new Error('That element could not be put on the sheet.');

      return api.setElementQuantity(projectId, input.sceneId, id, input.quantity);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['review-view', scriptVersionId] });
    },
  });

  /** Take a confirmed element off this scene's sheet. */
  const removeElement = useMutation({
    mutationFn: (input: { sceneId: string; productionElementId: string }) =>
      api.removeSceneElement(projectId, input.sceneId, input.productionElementId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['review-view', scriptVersionId] });
    },
  });

  /**
   * A phrase the producer selected, marked as a demand on the location.
   *
   * The four facets have been on the tagging surface since it was built with
   * nothing behind them: the handler returned early on anything that was not an
   * element, so pressing one did nothing at all and said nothing about why.
   */
  const tagFacet = useMutation({
    mutationFn: (input: { requirementId: string; facet: string; label: string }) =>
      api.addConstraint(projectId, input.requirementId, {
        facet: input.facet,
        label: input.label,
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['review-view', scriptVersionId] }),
        queryClient.invalidateQueries({ queryKey: ['requirements', projectId] }),
      ]);
    },
  });

  /**
   * SCENES PICKED ON THE RAIL, for the actions that already exist one at a
   * time (owner review 2026-10-27, point 5). The same shape as step 2's pick
   * boxes — a set of scene ids in component state, not in the URL: it is a
   * few seconds of intent before a press, not a view anybody returns to.
   */
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  const [bulkResult, setBulkResult] = useState<BulkSceneResult | null>(null);

  const pickedScenes = (review.data?.scenes ?? []).filter((s) => picked.has(s.id));
  // The undecided requirements on them, once each however many picked scenes
  // share one, read from the CURRENT rows: the review view's status can be a
  // render older than the decision somebody just made.
  const undecidedOnPicked = [...new Set(pickedScenes.flatMap((s) => s.requirements.map((r) => r.id)))]
    .map((id) => requirements.data?.find((row) => row.id === id))
    .filter((row): row is NonNullable<typeof row> => row !== undefined)
    .filter((row) => row.status === 'PROPOSED' || row.status === 'DRAFT');
  const unreviewedPicked = pickedScenes.filter((s) => s.reviewStatus !== 'REVIEWED');

  const bulk = useMutation({
    mutationFn: async (action: BulkSceneAction): Promise<BulkSceneResult> => {
      const refused: BulkSceneResult['refused'] = [];
      let done = 0;
      const reason = (error: unknown) =>
        error instanceof Error && error.message !== '' ? error.message : 'Refused — try it on its own card.';

      // One at a time, through the verbs a single press uses, so each row
      // keeps its own version check and its own answer.
      if (action === 'REVIEW') {
        for (const s of unreviewedPicked) {
          try {
            await api.markSceneReviewed(projectId, s.id);
            done += 1;
          } catch (error) {
            refused.push({ label: `Scene ${s.sceneNumber ?? String(s.ordinal)}`, reason: reason(error) });
          }
        }
      } else {
        for (const row of undecidedOnPicked) {
          try {
            if (action === 'APPROVE') await api.approveRequirement(projectId, row.id, row.rowVersion);
            else await api.dismissRequirement(projectId, row.id, row.rowVersion);
            done += 1;
          } catch (error) {
            refused.push({ label: row.title, reason: reason(error) });
          }
        }
      }

      return { action, done, refused };
    },
    onMutate: () => {
      setBulkResult(null);
    },
    onSuccess: async (result) => {
      setBulkResult(result);
      await refreshDecisions();
    },
  });

  /**
   * "THIS SCENE, OR EVERY SCENE?" — the question tagging and un-tagging both
   * ask when the answer would reach past one card (owner review 2026-10-27,
   * D2). Null while nothing is being asked. `sceneIds` is the list the dialog
   * shows and exactly the list a confirmation sends.
   */
  const [elementScope, setElementScope] = useState<
    | null
    | { mode: 'TAG'; name: string; category: ElementCategory; sourceSceneId: string; sceneIds: string[] }
    | { mode: 'REMOVE'; name: string; productionElementId: string; sourceSceneId: string; sceneIds: string[] }
  >(null);

  const tagEverywhere = useMutation({
    mutationFn: (input: { category: ElementCategory; name: string; sceneIds: readonly string[] }) =>
      api.addElementToScenes(projectId, input),
    onSuccess: async () => {
      setElementScope(null);
      await queryClient.invalidateQueries({ queryKey: ['review-view', scriptVersionId] });
    },
  });

  const removeEverywhere = useMutation({
    mutationFn: (input: { productionElementId: string; sceneIds: readonly string[] }) =>
      api.removeElementFromScenes(projectId, input.productionElementId, input.sceneIds),
    onSuccess: async () => {
      setElementScope(null);
      await queryClient.invalidateQueries({ queryKey: ['review-view', scriptVersionId] });
    },
  });

  /** A phrase the producer selected in the text, tagged as an element. */
  const tagElement = useMutation({
    mutationFn: (input: { sceneId: string; category: ElementCategory; name: string }) =>
      api.addSceneElement(projectId, input.sceneId, input.category, input.name),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['review-view', scriptVersionId] });
    },
  });

  const addRequirement = useMutation({
    mutationFn: (input: { title: string; sceneIds: readonly string[] }) =>
      api.addRequirement(projectId, { title: input.title, sceneIds: input.sceneIds }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['review-view', scriptVersionId] });
    },
  });

  const data: ReviewRequirementsData | null = useMemo(() => {
    const view = review.data;
    if (!view) return null;

    const statusById = new Map((requirements.data ?? []).map((r) => [r.id, r.status]));
    // The full rows carry what each requirement must be like — its values and
    // its creative brief — which the review view does not repeat.
    const rowById = new Map((requirements.data ?? []).map((r) => [r.id, r]));

    const scenes: SceneCardScene[] = view.scenes.map((scene) => {
      /**
       * BOTH HALVES OF THE SHEET, merged the way the Script & Scenes panel
       * merges them: what the production has confirmed first, then what the
       * model proposed and nobody has confirmed.
       *
       * The card used to render `marks` alone — the model's readings — so an
       * element a producer tagged from the script text was written to
       * `production_element`, stored correctly, and never shown. The press
       * appeared to do nothing at all.
       *
       * A confirmed element SUPERSEDES its proposal, or the same prop appears
       * twice: once as a suggestion and once as a decision.
       */
      const elements: Partial<Record<ElementCategory, SceneElement[]>> = {};
      /**
       * READ DEFENSIVELY ACROSS THE VERSION BOUNDARY.
       *
       * `elements` and the mark fields beside it are newer than some servers
       * this build will meet: a rolling deploy runs the new SPA against the old
       * API for a while, and a rollback runs it indefinitely. Reading them
       * unguarded turned that window into a blank screen with a TypeError —
       * the whole breakdown gone rather than the one new column missing.
       */
      const confirmed = scene.elements ?? [];
      const marks = scene.marks ?? [];
      const confirmedKeys = new Set(
        confirmed.map((e) => `${e.category}:${normalizeElementName(e.name)}`),
      );

      /**
       * THE TEXT AND THE SHEET SHOW THE SAME THING.
       *
       * The highlight used to be painted from the model's marks alone, and
       * from ALL of them — so a phrase a producer tagged by hand was on the
       * sheet and not in the text, and one they removed was off the sheet and
       * still coloured in it. Both directions of that are a screen contradicting
       * itself about what the breakdown says.
       *
       * The text is now painted from what is on the sheet: confirmed elements
       * included, removed readings left out. `markUp` matches by the phrase
       * itself rather than by stored offsets, so a hand-added element paints
       * wherever those words appear without needing a span of its own.
       */
      const painted: Paintable[] = [];

      for (const element of confirmed) {
        (elements[element.category] ??= []).push({
          id: element.id,
          text: element.name,
          provenance: 'HUMAN_CONFIRMED',
          decision: null,
          quantity: element.quantity,
        });
        painted.push({ text: element.name, category: element.category, unconfirmed: false });
      }

      for (const mark of marks) {
        if (confirmedKeys.has(`${mark.category}:${normalizeElementName(mark.text)}`)) continue;

        (elements[mark.category] ??= []).push({
          id: mark.id,
          text: mark.text,
          provenance: 'AI_INFERENCE',
          decision: mark.decision,
          // A proposal carries no count until somebody makes one, and making
          // one is what puts it on the sheet. A count the script states is a
          // hint beside the empty field, never the count itself.
          quantity: null,
          quantityHint: mark.quantityHint ?? null,
        });

        // A removed reading is off the sheet, so it is out of the text too. The
        // name is painted where the text says it; a name the text does not
        // contain ("CROWD" for "dozens of onlookers") paints the evidence the
        // server rebuilt instead, so the reading is still visible where it was read.
        if (mark.decision !== 'REJECT') {
          const body = scene.text ?? '';
          const evidence = mark.evidence?.text ?? '';
          const phrase = body.includes(mark.text) || evidence === '' || !body.includes(evidence) ? mark.text : evidence;
          painted.push({ text: phrase, category: mark.category, unconfirmed: mark.unconfirmed || mark.evidence?.precision === 'EVIDENCE_REVIEW' });
        }
      }

      return {
        id: scene.id,
        sceneNumber: scene.sceneNumber,
        ordinal: scene.ordinal,
        slugline: scene.slugline,
        intExt: scene.intExt,
        timeOfDay: scene.timeOfDay,
        requirements: scene.requirements.map((r) => {
          const row = rowById.get(r.id);

          return {
            id: r.id,
            title: r.title,
            status: statusById.get(r.id) ?? r.status,
            confidenceBand: r.confidenceBand,
            sharedWithSceneNumbers: r.sharedWithSceneNumbers,
            // READ DEFENSIVELY: `facet` is on every constraint row the server
            // sends, and absent from older payloads a rolled-back API answers with.
            constraints: (row?.constraints ?? []).map((c) => {
              const facet: unknown = (c as { facet?: unknown }).facet;

              return { id: c.id, kind: c.kind, facet: typeof facet === 'string' ? facet : null, label: c.label };
            }),
            creativeDescription: row?.creativeDescription ?? null,
          };
        }),
        locationRecognised: scene.requirements.length > 0,
        recognitionNote:
          scene.requirements.length > 0
            ? scene.requirements.map((r) => r.title).join(' · ')
            : 'No location noun resolved for this scene',
        // The element colours are the detailed breakdown's: on the Locations
        // page they would paint "MARA" in Cast red with no legend there to say so.
        lines: scene.text === null ? [] : toLines(scene.text, focus === 'LOCATIONS' ? [] : painted),
        elements,
        reviewStatus: scene.reviewStatus as SceneCardScene['reviewStatus'],
      };
    });

    const railScenes: RailScene[] = view.scenes.map((scene) => {
      const shared = scene.requirements.flatMap((r) => r.sharedWithSceneNumbers);

      // THE DETAILED BREAKDOWN'S RAIL SAYS WHERE ITS OWN WORK STANDS. A
      // location that did not resolve is a locations fact; here the question is
      // whether this scene's sheet has been reviewed.
      if (focus === 'ELEMENTS') {
        return {
          id: scene.id,
          sceneNumber: scene.sceneNumber,
          ordinal: scene.ordinal,
          slugline: scene.slugline,
          state: scene.reviewStatus === 'REVIEWED' ? ('REVIEWED' as const) : ('ATTENTION' as const),
          confidence: 'RESOLVED' as const,
          note:
            scene.reviewStatus === 'REVIEWED'
              ? 'Reviewed'
              : scene.reviewStatus === 'IN_REVIEW'
                ? 'Changed since it was reviewed'
                : 'Not reviewed yet',
          sharedWithSceneNumbers: [],
          sharedUnresolved: false,
        };
      }

      return {
        id: scene.id,
        sceneNumber: scene.sceneNumber,
        ordinal: scene.ordinal,
        slugline: scene.slugline,
        confidence: confidenceOf(scene),
        note:
          scene.requirements.length > 0
            ? scene.requirements.map((r) => r.title).join(' · ')
            : 'No location noun resolved',
        sharedWithSceneNumbers: [...new Set(shared)],
        sharedUnresolved: scene.requirements.length === 0 && shared.length > 0,
        // A scene needs the producer while it has no location, or one of its
        // locations still awaits a decision — read from the CURRENT rows.
        state:
          scene.requirements.length === 0 ||
          scene.requirements.some((r) => UNDECIDED.has(statusById.get(r.id) ?? r.status))
            ? ('ATTENTION' as const)
            : ('REVIEWED' as const),
        requirementCount: scene.requirements.length,
      };
    });

    // This draft's requirements, each once, with the status that is current.
    const draft = view.requirements
      .map((r) => statusById.get(r.id) ?? r.status)
      .filter((status) => status !== 'SUPERSEDED');

    return {
      projectTitle,
      scriptVersionLabel,
      ready: true,
      requirementCount: view.requirements.length,
      shownRequirementCount: view.requirements.length,
      railScenes,
      scenes,
      summary: {
        awaiting: draft.filter((status) => UNDECIDED.has(status)).length,
        approved: draft.filter((status) => status === 'APPROVED').length,
        notNeeded: draft.filter((status) => status === 'DISMISSED').length,
        scenesWithoutRequirement: view.scenes.filter((scene) => scene.requirements.length === 0).length,
      },
    };
  }, [review.data, requirements.data, projectTitle, scriptVersionLabel, focus]);

  const runState = reviewRunStateOf(progress, reading);

  if (review.isPending) {
    return (
      <p className="mm" data-testid="review-loading" style={{ fontSize: 13 }}>
        Loading the breakdown…
      </p>
    );
  }

  if (review.isError || data === null) {
    return (
      <p data-testid="review-load-error" style={{ fontSize: 13, color: 'var(--danger)' }}>
        {review.error instanceof ApiError
          ? review.error.message
          : 'The breakdown could not be loaded.'}
      </p>
    );
  }

  const sceneById = new Map((review.data?.scenes ?? []).map((s) => [s.id, s]));

  return (
    <>
      <ReviewRequirementsScreen
        data={data}
        projectId={projectId}
        busy={
          addRequirement.isPending ||
          markReviewed.isPending ||
          unmarkReviewed.isPending ||
          decideElement.isPending ||
          tagFacet.isPending ||
          removeElement.isPending ||
          setQuantity.isPending ||
          tagElement.isPending ||
          merge.isPending ||
          split.isPending ||
          decide.isPending ||
          restore.isPending
        }
        error={
          merge.error instanceof ApiError
            ? merge.error.message
            : split.error instanceof ApiError
            ? split.error.message
            : tagFacet.error instanceof ApiError
            ? tagFacet.error.message
            : setQuantity.error instanceof ApiError
            ? setQuantity.error.message
            : removeElement.error instanceof ApiError
            ? removeElement.error.message
            : decideElement.error instanceof ApiError
            ? decideElement.error.message
            : tagElement.error instanceof ApiError
            ? tagElement.error.message
            : unmarkReviewed.error instanceof ApiError
            ? unmarkReviewed.error.message
            : addRequirement.error instanceof ApiError
            ? addRequirement.error.message
            : addRequirement.error
              ? 'That could not be saved.'
              : decide.error instanceof ApiError || decide.error instanceof Error
                ? decide.error.message
                : restore.error instanceof ApiError || restore.error instanceof Error
                  ? restore.error.message
                  : null
        }
        onAddRequirement={(input) => addRequirement.mutate(input)}
        onMarkSceneReviewed={(sceneId) => markReviewed.mutate(sceneId)}
        onUnmarkSceneReviewed={(sceneId) => unmarkReviewed.mutate(sceneId)}
        onAddElement={(sceneId, category, name) => {
          tagElement.mutate({ sceneId, category, name });
        }}
        onDecideElement={(mentionId, decision) => decideElement.mutate({ mentionId, decision })}
        onRemoveElement={(sceneId, productionElementId) => {
          // On more than this scene, the question is asked; on this one alone,
          // there is nothing to ask and it comes off as it always did.
          const on = (review.data?.scenes ?? []).filter((s) =>
            (s.elements ?? []).some((e) => e.id === productionElementId),
          );
          const name = on.flatMap((s) => s.elements ?? []).find((e) => e.id === productionElementId)?.name;

          if (on.length <= 1 || name === undefined) {
            removeElement.mutate({ sceneId, productionElementId });

            return;
          }

          removeEverywhere.reset();
          setElementScope({
            mode: 'REMOVE',
            name,
            productionElementId,
            sourceSceneId: sceneId,
            sceneIds: on.map((s) => s.id),
          });
        }}
        onSetQuantity={(sceneId, element, quantity) => {
          setQuantity.mutate({ sceneId, element, quantity });
        }}
        onTag={(sceneId, selection, target, scope) => {
          if (target.kind === 'ELEMENT') {
            // Every scene whose text names it, the tagged scene always among
            // them — it is where the words were selected, even when the stored
            // text is a capped excerpt that stops short of them.
            const everywhere =
              scope === 'EVERY_SCENE'
                ? (review.data?.scenes ?? [])
                    .filter((s) => s.id === sceneId || appearsInText(s.text, selection))
                    .map((s) => s.id)
                : [sceneId];

            if (everywhere.length <= 1) {
              tagElement.mutate({ sceneId, category: target.category, name: selection });

              return;
            }

            tagEverywhere.reset();
            setElementScope({
              mode: 'TAG',
              name: selection,
              category: target.category,
              sourceSceneId: sceneId,
              sceneIds: everywhere,
            });

            return;
          }

          // A facet belongs to the scene's location requirement, and the card
          // only offers these when there is exactly one to attach to.
          const requirement = review.data?.scenes.find((s) => s.id === sceneId)?.requirements[0];
          if (requirement === undefined) return;

          tagFacet.mutate({ requirementId: requirement.id, facet: target.facet, label: selection });
        }}
        onDecideRequirement={(requirementId, decision) => decide.mutate({ requirementId, decision })}
        onRestoreRequirement={(requirementId) => restore.mutate(requirementId)}
        settled={settled}
        onMerge={(requirementIds) => merge.mutate(requirementIds)}
        onSplit={(requirementId, input) => {
          split.mutate({ requirementId, title: input.title, sceneIds: input.sceneIds });
        }}
        onContinueToMatching={onContinueToMatching}
        continueBlockedReason={continueBlockedReason}
        continueNote={continueNote}
        focus={focus}
        continueLabel={continueLabel}
        runState={runState}
        runNote={
          runState === 'FAILED'
            ? extractionFailureSentence(progress)
            : runState === 'PARTIAL'
              ? didNotFit > 0
                ? `${String(didNotFit)} ${didNotFit === 1 ? 'scene was' : 'scenes were'} not read — the screenplay is longer than one request can carry. Pick them on the left and read them on their own; what was found stays as it is.`
                : `${String(withoutElements)} ${withoutElements === 1 ? 'scene was' : 'scenes were'} not fully read — the AI got no answer for some of their departments, so their elements are incomplete. Run the analysis again, or tag what is missing by hand; what was found stays as it is.`
              : null
        }
        initialSceneId={initialSceneId}
        onSceneChange={onSceneChange}
        /*
         * TWO REASONS TO OFFER A READING, and the second is not "the page is
         * empty": a screenplay too long for one request has requirements for its
         * first part and none for the rest, so the count is not zero and those
         * scenes were still never read. Past both, this page is for correcting
         * what the model produced, and a "read it again" button beside reviewed
         * work invites paying twice to overwrite corrections made by hand.
         */
        /*
         * While a run is going this slot carries the indicator instead of the
         * control: offering "Analyse again" beside a running breakdown invites a
         * second paid request for the answer already on its way, and step 3 is
         * exactly where a producer sits watching an empty headline.
         */
        pickedSceneIds={picked}
        onPickScenes={(sceneIds, on) => {
          setBulkResult(null);
          setPicked((current) => {
            const next = new Set(current);

            for (const id of sceneIds) {
              if (on) next.add(id);
              else next.delete(id);
            }

            return next;
          });
        }}
        bulkActions={
          picked.size === 0 ? null : (
            <BulkSceneActions
              pickedCount={pickedScenes.length}
              undecidedCount={undecidedOnPicked.length}
              unreviewedCount={unreviewedPicked.length}
              busy={bulk.isPending}
              result={bulkResult}
              onApprove={() => {
                bulk.mutate('APPROVE');
              }}
              onDismiss={() => {
                bulk.mutate('DISMISS');
              }}
              onMarkReviewed={() => {
                bulk.mutate('REVIEW');
              }}
              onClear={() => {
                setPicked(new Set());
                setBulkResult(null);
              }}
            />
          )
        }
        analyse={
          // Re-reading, the slot stays empty (the indicator ends with its run):
          // offering "Analyse" over a pre-run answer would sell the reading twice.
          reading ? (
            <BreakdownProgress scriptVersionId={scriptVersionId} />
          ) : shouldOfferAnalysis({
            // The detailed breakdown has no requirement count to go on: what
            // it needs from the reading is that one happened.
            requirementCount: focus === 'ELEMENTS' ? (extractionHasRun(progress) ? 1 : 0) : data.requirementCount,
            scenesDidNotFit: didNotFit,
            scenesWithoutElements: withoutElements,
            lastRunFailed: progress?.status === 'FAILED' && parseFailure(progress) === null,
          }) ? (
            <AnalyseWithAi
              projectId={projectId}
              versionId={scriptVersionId}
              sceneCount={data.scenes.length}
              reads={focus === 'ELEMENTS' ? 'ELEMENTS' : 'LOCATIONS'}
              // SCENES PICKED ON THE RAIL ARE READ ON THEIR OWN. This is where
              // "pick the scenes that did not fit and run them" happens since
              // step 2 stopped being about the AI (2026-10-29): a screenplay
              // too long for one request is finished in selections from here.
              sceneIds={picked.size > 0 ? [...picked] : undefined}
              onStarted={() => {
                // The picks have been spent; left ticked they invite a second
                // paid request for the same scenes.
                setPicked(new Set());
                setBulkResult(null);
              }}
            />
          ) : null
        }
      />

      {elementScope === null ? null : (
        <ElementScopeDialog
          mode={elementScope.mode}
          name={elementScope.name}
          sourceSceneId={elementScope.sourceSceneId}
          scenes={elementScope.sceneIds.map((id) => ({
            id,
            sceneNumber: sceneById.get(id)?.sceneNumber ?? null,
            slugline: sceneById.get(id)?.slugline ?? null,
          }))}
          busy={tagEverywhere.isPending || removeEverywhere.isPending}
          error={
            (elementScope.mode === 'TAG' ? tagEverywhere.error : removeEverywhere.error)?.message ?? null
          }
          onCancel={() => {
            setElementScope(null);
          }}
          onThisScene={() => {
            const ask = elementScope;
            setElementScope(null);

            if (ask.mode === 'TAG') {
              tagElement.mutate({ sceneId: ask.sourceSceneId, category: ask.category, name: ask.name });
            } else {
              removeElement.mutate({ sceneId: ask.sourceSceneId, productionElementId: ask.productionElementId });
            }
          }}
          onAllScenes={() => {
            if (elementScope.mode === 'TAG') {
              tagEverywhere.mutate({
                category: elementScope.category,
                name: elementScope.name,
                sceneIds: elementScope.sceneIds,
              });
            } else {
              removeEverywhere.mutate({
                productionElementId: elementScope.productionElementId,
                sceneIds: elementScope.sceneIds,
              });
            }
          }}
        />
      )}
    </>
  );
}
